import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight, Camera, MapPin, Share2, Play, BadgeCheck, MoreHorizontal, Building2, Expand } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useIsBelowLg } from '@/hooks/use-mobile';
import { SwipeableZoomImage, useSwipeNav } from '@/components/ui/swipeable-zoom-image';

interface MarketedBy {
  name: string;
  logoUrl?: string | null;
  verified?: boolean;
}

interface ProjectImageGalleryProps {
  images: any[];
  name: string;
  developerLogo?: string | null;
  developerName?: string;
  marketedBy?: MarketedBy | null;
  videoCount?: number;
  onOpenMap?: () => void;
  priceLabel?: string;
  location?: string;
}

export function ProjectImageGallery({
  images,
  name,
  developerLogo,
  developerName,
  marketedBy,
  videoCount = 0,
  onOpenMap,
  priceLabel,
  location,
}: ProjectImageGalleryProps) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [mediaFilter, setMediaFilter] = useState<'all' | 'photos' | 'videos'>('all');
  const isMobile = useIsBelowLg();
  const fallback = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=600&fit=crop';

  const isVideo = (img: any) => !!img && (img.type === 'video' || img.media_type === 'video');
  const posterOf = (img: any) =>
    img?.thumbnail_url || img?.thumbnailUrl || img?.poster_url || img?.posterUrl || (isVideo(img) ? null : img?.url) || fallback;

  const current = images[selectedImage];
  const currentIsVideo = isVideo(current);
  const mainImg = posterOf(current);
  const photoCount = images.length;
  const derivedVideoCount = images.filter(isVideo).length;
  const totalVideoCount = videoCount || derivedVideoCount;
  const photoOnlyCount = photoCount - derivedVideoCount;

  const filteredImages = mediaFilter === 'photos'
    ? images.filter(img => !isVideo(img))
    : mediaFilter === 'videos'
      ? images.filter(isVideo)
      : images;
  const filteredIdx = Math.max(0, filteredImages.indexOf(current));
  const goRelative = (delta: number) => {
    if (!filteredImages.length) return;
    const base = filteredIdx >= 0 ? filteredIdx : 0;
    const next = (base + delta + filteredImages.length) % filteredImages.length;
    setSelectedImage(images.indexOf(filteredImages[next]));
  };

  const applyFilter = (f: 'all' | 'photos' | 'videos') => {
    setMediaFilter(f);
    const pool = f === 'photos' ? images.filter(img => !isVideo(img))
      : f === 'videos' ? images.filter(isVideo)
      : images;
    if (pool.length && !pool.includes(current)) {
      setSelectedImage(images.indexOf(pool[0]));
    }
  };

  // Default the "Marketed By" badge to developer when no agency provided.
  const badge: MarketedBy | null =
    marketedBy ?? (developerName ? { name: developerName, logoUrl: developerLogo, verified: true } : null);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
  };

  const showFilter = derivedVideoCount > 0 && photoOnlyCount > 0;
  const filterLabel = mediaFilter === 'photos' ? 'photos' : mediaFilter === 'videos' ? 'videos' : 'media';
  const seeAllCount = mediaFilter === 'photos' ? photoOnlyCount : mediaFilter === 'videos' ? derivedVideoCount : photoCount;
  const seeAllLabel = `See all ${seeAllCount} ${filterLabel}`;
  const swipeHandlers = useSwipeNav(() => goRelative(-1), () => goRelative(1));

  const filterOptions = [
    { key: 'all' as const, label: `All ${photoCount}`, icon: null, aria: `Show all ${photoCount} media items` },
    { key: 'photos' as const, label: `${photoOnlyCount}`, icon: <Camera className="h-3 w-3" aria-hidden="true" />, aria: `Show ${photoOnlyCount} photos only` },
    { key: 'videos' as const, label: `${derivedVideoCount}`, icon: <Play className="h-3 w-3 fill-current" aria-hidden="true" />, aria: `Show ${derivedVideoCount} videos only` },
  ];

  // Inline filter toolbar rendered above the thumbnail strip (non-overlay).
  const renderFilterToolbar = (size: 'sm' | 'md' = 'md') => {
    if (!showFilter) return null;
    const h = size === 'sm' ? 'h-7' : 'h-8';
    const px = size === 'sm' ? 'px-2.5' : 'px-3';
    return (
      <div
        role="group"
        aria-label="Filter thumbnails by media type"
        className="inline-flex items-center rounded-full bg-muted p-0.5 text-xs font-medium border border-border"
      >
        {filterOptions.map(opt => (
          <button
            key={opt.key}
            type="button"
            aria-label={opt.aria}
            aria-pressed={mediaFilter === opt.key}
            onClick={() => applyFilter(opt.key)}
            className={`${px} ${h} rounded-full transition-colors flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${mediaFilter === opt.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {opt.key === 'all' ? <span aria-hidden="true">All</span> : opt.icon}
            <span>{opt.label}</span>
          </button>
        ))}
      </div>
    );
  };

  // Horizontal thumbnail strip — filters thumbnails without opening lightbox.
  const renderThumbStrip = () => {
    if (filteredImages.length <= 1) return null;
    return (
      <TooltipProvider delayDuration={150}>
        <div className="flex gap-2 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:thin]">
          {filteredImages.map((img, i) => {
            const stripIsVideo = isVideo(img);
            const originalIdx = images.indexOf(img);
            const isSelected = originalIdx === selectedImage;
            const kind = stripIsVideo ? 'Video' : 'Photo';
            const tip = `${kind} ${i + 1} of ${filteredImages.length}`;
            return (
              <Tooltip key={img?.id ?? i}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setSelectedImage(originalIdx)}
                    onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })}
                    aria-label={tip}
                    aria-pressed={isSelected}
                    className={`relative shrink-0 w-[96px] h-[64px] rounded-md overflow-hidden transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:opacity-100 ${isSelected ? 'ring-2 ring-primary opacity-100' : 'opacity-70 hover:opacity-100'}`}
                  >
                    <img src={posterOf(img)} alt="" className="w-full h-full object-cover" />
                    {stripIsVideo && (
                      <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="h-6 w-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
                          <Play className="h-3 w-3 text-white fill-white" />
                        </span>
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="flex items-center gap-1.5 text-xs">
                  {stripIsVideo ? <Play className="h-3 w-3 fill-current" /> : <Camera className="h-3 w-3" />}
                  <span>{tip}</span>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    );
  };

  const filterPill = showFilter ? (
    <div
      role="group"
      aria-label="Filter gallery by media type"
      className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center rounded-full bg-black/70 backdrop-blur-md p-0.5 text-[11px] font-semibold shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      {filterOptions.map(opt => (
        <button
          key={opt.key}
          type="button"
          aria-label={opt.aria}
          aria-pressed={mediaFilter === opt.key}
          onClick={(e) => { e.stopPropagation(); applyFilter(opt.key); }}
          className={`px-2.5 h-6 rounded-full transition-colors flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black/40 ${mediaFilter === opt.key ? 'bg-white text-foreground' : 'text-white/85 hover:text-white'}`}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  ) : null;

  // Bottom-left count badges — also act as filter shortcuts (keyboard accessible).
  const renderCountBadges = (compact = false) => {
    const photoActive = mediaFilter === 'photos';
    const videoActive = mediaFilter === 'videos';
    const baseFocus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black/40';
    return (
      <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
        <button
          type="button"
          aria-label={`Show photos only (${photoOnlyCount > 0 ? photoOnlyCount : photoCount} ${photoOnlyCount === 1 ? 'photo' : 'photos'})`}
          aria-pressed={photoActive}
          onClick={(e) => { e.stopPropagation(); applyFilter(photoActive ? 'all' : 'photos'); }}
          className={`${baseFocus} flex items-center gap-1 text-white text-xs px-2.5 py-1 rounded transition-colors ${photoActive ? 'bg-primary' : 'bg-black/70 hover:bg-black/85'}`}
        >
          <Camera className="h-3.5 w-3.5" aria-hidden="true" /> {photoOnlyCount > 0 ? photoOnlyCount : photoCount} {compact ? '' : 'Photos'}
        </button>
        {totalVideoCount > 0 && (
          <button
            type="button"
            aria-label={`Show videos only (${totalVideoCount} ${totalVideoCount === 1 ? 'video' : 'videos'})`}
            aria-pressed={videoActive}
            onClick={(e) => { e.stopPropagation(); applyFilter(videoActive ? 'all' : 'videos'); }}
            className={`${baseFocus} flex items-center gap-1 text-destructive-foreground text-xs font-semibold px-2.5 py-1 rounded transition-colors ${videoActive ? 'bg-destructive ring-2 ring-white' : 'bg-destructive hover:brightness-110'}`}
          >
            <Play className="h-3 w-3 fill-current" aria-hidden="true" /> {totalVideoCount} {compact ? '' : (totalVideoCount === 1 ? 'Video' : 'Videos')}
          </button>
        )}
      </div>
    );
  };


  const lightbox = (
    <Dialog open={overviewOpen} onOpenChange={setOverviewOpen}>
      <DialogContent className="max-w-[min(1280px,95vw)] w-[95vw] h-[92vh] p-0 bg-[#0b0b0b] border-none overflow-hidden flex flex-col gap-0 [&>button]:text-white [&>button]:opacity-80 [&>button:hover]:opacity-100 [&>button]:top-4 [&>button]:right-5 [&>button]:z-20">
        <div className="flex items-center justify-between gap-3 px-5 lg:px-7 h-14 shrink-0 border-b border-white/10 text-white">
          <div className="min-w-0 pr-2">
            <div className="text-[11px] uppercase tracking-wider text-white/60">
              {currentIsVideo ? 'Video' : 'Photo'}
            </div>
            <h3 className="text-sm font-semibold truncate">{name}</h3>
          </div>

          {/* Media filter toggle */}
          {showFilter && (
            <div role="group" aria-label="Filter gallery by media type" className="inline-flex items-center rounded-full bg-white/10 p-0.5 text-xs font-medium">
              {filterOptions.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  aria-label={opt.aria}
                  aria-pressed={mediaFilter === opt.key}
                  onClick={() => applyFilter(opt.key)}
                  className={`px-3 h-7 rounded-full transition-colors flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${mediaFilter === opt.key ? 'bg-white text-foreground' : 'text-white/80 hover:text-white'}`}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          <div className="text-xs font-medium text-white/80 tabular-nums shrink-0">
            {filteredIdx + 1} <span className="text-white/40">/ {filteredImages.length}</span>
          </div>
        </div>
        <div className="relative flex-1 min-h-0 bg-black flex items-center justify-center">
          {currentIsVideo ? (
            <video key={current?.id ?? selectedImage} src={current?.url} poster={posterOf(current)} controls autoPlay playsInline className="max-h-full max-w-full" />
          ) : (
            <SwipeableZoomImage
              images={filteredImages.map(posterOf)}
              index={Math.max(0, filteredIdx)}
              onIndexChange={(i) => setSelectedImage(images.indexOf(filteredImages[i]))}
              alt={name}
            />
          )}
          {filteredImages.length > 1 && !currentIsVideo && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition z-20" onClick={() => goRelative(-1)} aria-label="Previous">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition z-20" onClick={() => goRelative(1)} aria-label="Next">
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
        <div className="shrink-0 border-t border-white/10 bg-[#111] px-3 py-3">
          <div className="flex gap-2 overflow-x-auto scroll-smooth [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.3)_transparent]">
            {filteredImages.map((img, i) => {
              const stripIsVideo = isVideo(img);
              const originalIdx = images.indexOf(img);
              return (
                <button key={img.id ?? i} onClick={() => setSelectedImage(originalIdx)} className={`relative shrink-0 w-[120px] h-[80px] rounded-md overflow-hidden transition-all ${originalIdx === selectedImage ? 'ring-2 ring-primary opacity-100' : 'opacity-55 hover:opacity-90'}`}>
                  <img src={posterOf(img)} alt="" className="w-full h-full object-cover" />
                  {stripIsVideo && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="h-7 w-7 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
                        <Play className="h-3.5 w-3.5 text-white fill-white" />
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (isMobile) {
    return (
      <>
      <div className="-mx-4 mb-4">
        <div className="relative aspect-[16/10] bg-muted overflow-hidden cursor-pointer group touch-pan-y" onClick={() => setOverviewOpen(true)} {...swipeHandlers}>
          <img src={mainImg} alt={name} className="w-full h-full object-cover" />
          {currentIsVideo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="h-16 w-16 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center shadow-lg ring-1 ring-white/20">
                <Play className="h-7 w-7 text-white fill-white" />
              </span>
            </div>
          )}

          {badge && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-md pl-2 pr-1.5 py-1 flex items-center gap-1.5 text-[11px] font-medium shadow-sm">
              <span className="text-muted-foreground">Marketed By</span>
              {badge.logoUrl ? (
                <img src={badge.logoUrl} alt={badge.name} className="h-4 w-4 rounded object-cover" />
              ) : (
                <span className="font-semibold">{badge.name}</span>
              )}
              {badge.verified && <BadgeCheck className="h-3.5 w-3.5 text-primary fill-primary/10" />}
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); handleShare(); }}
            className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full p-1.5 shadow-sm"
          >
            <Share2 className="h-4 w-4 text-foreground" />
          </button>

          {filterPill}

          {filteredImages.length > 1 && (
            <>
              <button
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full h-8 w-8 flex items-center justify-center shadow-sm"
                onClick={(e) => { e.stopPropagation(); goRelative(-1); }}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full h-8 w-8 flex items-center justify-center shadow-sm"
                onClick={(e) => { e.stopPropagation(); goRelative(1); }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {renderCountBadges(true)}

          <button
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white text-foreground text-xs font-semibold px-3 py-1.5 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            onClick={(e) => { e.stopPropagation(); setOverviewOpen(true); }}
            aria-label={seeAllLabel}
          >
            <Expand className="h-3.5 w-3.5" aria-hidden="true" />
            See all {seeAllCount}
          </button>
        </div>
      </div>
      <div className="px-1 mb-4 flex items-center justify-between gap-2">
        {renderFilterToolbar('sm')}
        <span className="text-xs text-muted-foreground tabular-nums">{filteredIdx + 1} / {filteredImages.length}</span>
      </div>
      {lightbox}
      </>
    );
  }

  // Desktop: Zillow-style hero — 1 main image + 2x2 thumbnail grid (driven by filter)
  const heroPool = filteredImages.length ? filteredImages : images;
  const sideThumbs = heroPool.slice(1, 5);
  const heroCount = heroPool.length;

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-1 rounded-xl overflow-hidden bg-muted">
        {/* Main image */}
        <div
          className="relative aspect-[16/10] md:aspect-auto md:h-[460px] overflow-hidden cursor-pointer group touch-pan-y"
          onClick={() => setOverviewOpen(true)}
          {...swipeHandlers}
        >
          <img src={mainImg} alt={name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />

          {currentIsVideo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="h-16 w-16 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center shadow-lg ring-1 ring-white/20">
                <Play className="h-7 w-7 text-white fill-white" />
              </span>
            </div>
          )}

          {filteredImages.length > 1 && (
            <>
              <Button variant="secondary" size="icon" className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); goRelative(-1); }}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="secondary" size="icon" className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); goRelative(1); }}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}

          {filterPill}

          <button
            onClick={(e) => { e.stopPropagation(); handleShare(); }}
            className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-sm hover:bg-white transition-colors"
          >
            <Share2 className="h-4 w-4 text-foreground" />
          </button>

          {renderCountBadges()}

          <button
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white text-foreground text-xs font-semibold px-3.5 py-2 shadow-lg hover:bg-white/95 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            onClick={(e) => { e.stopPropagation(); setOverviewOpen(true); }}
            aria-label={seeAllLabel}
          >
            <Expand className="h-3.5 w-3.5" aria-hidden="true" />
            {seeAllLabel}
          </button>

          {badge && (
            <div className="absolute top-3 left-3 bg-white rounded-md pl-2.5 pr-2 py-1.5 flex items-center gap-1.5 text-xs font-medium shadow-md">
              <span className="text-muted-foreground">Marketed By</span>
              {badge.logoUrl ? (
                <img src={badge.logoUrl} alt={badge.name} className="h-4 w-auto max-w-[80px] object-contain" />
              ) : (
                <span className="font-semibold text-foreground">{badge.name}</span>
              )}
              {badge.verified && (
                <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary text-primary-foreground">
                  <BadgeCheck className="h-3 w-3" />
                </span>
              )}
            </div>
          )}
        </div>

        {/* 2x2 side thumbnails (desktop) */}
        {heroCount > 1 && (
          <div className="hidden md:grid grid-cols-2 grid-rows-2 gap-1 h-[460px]">
            {sideThumbs.map((img: any, i: number) => {
              const originalIdx = images.indexOf(img);
              const isLastWithMore = i === 3 && heroCount > 5;
              const thumbIsVideo = isVideo(img);
              const isSelected = originalIdx === selectedImage;
              return (
                <button
                  key={img?.id || i}
                  onClick={() => { if (isLastWithMore) { setOverviewOpen(true); } else { setSelectedImage(originalIdx); setOverviewOpen(true); } }}
                  className={`relative overflow-hidden ${isSelected ? 'ring-2 ring-primary' : 'opacity-90 hover:opacity-100'} transition-all`}
                >
                  <img src={posterOf(img)} alt="" className="w-full h-full object-cover" />
                  {thumbIsVideo && !isLastWithMore && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="h-11 w-11 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
                        <Play className="h-5 w-5 text-white fill-white" />
                      </span>
                    </div>
                  )}
                  {isLastWithMore && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-bold">
                      <span className="flex items-center gap-1.5 text-sm">
                        <Expand className="h-4 w-4" />
                        +{heroCount - 5} more
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showFilter && (
        <div className="mt-3 flex items-center justify-between gap-3">
          {renderFilterToolbar('md')}
          <span className="text-xs text-muted-foreground tabular-nums">
            Showing {filteredImages.length} of {photoCount}
          </span>
        </div>
      )}

      {lightbox}
    </div>
  );
}
